import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Drawer,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SearchIcon from "@mui/icons-material/Search";
import WebhookRoundedIcon from "@mui/icons-material/WebhookRounded";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";
import ClearIcon from "@mui/icons-material/Clear";
import EndpointFormDialog from "./webhooks/EndpointFormDialog";
import {
  WEBHOOKS_API,
  deleteRequest,
  getErrorMessage,
  getRequest,
  patchRequest,
  postRequest,
} from "./webhooks/api";
import {
  deliveryStatusChip,
  panelSx,
  scrollableTableContainerSx,
  stickyTableHeadSx,
} from "./webhooks/WebhooksCommon";
import { can } from "../../../auth/can";
import { useAuth } from "../../../auth/useAuth";
import { useNotification } from "../../../context/NotificationContext";
import { Close } from "@mui/icons-material";

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
}

function formatMetric(value, suffix = "") {
  if (value === null || value === undefined || Number.isNaN(Number(value)))
    return "-";
  return `${Number(value).toLocaleString()}${suffix}`;
}

function formatMs(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value)))
    return "-";
  if (value >= 1000) return `${(Number(value) / 1000).toFixed(2)}s`;
  return `${Math.round(Number(value))}ms`;
}

function compactId(value) {
  if (!value) return "-";
  const text = String(value);
  return text.length > 12 ? `${text.slice(0, 8)}...${text.slice(-4)}` : text;
}

function eventDeviceLabel(row) {
  if (row.device?.name) return row.device.name;
  if (row.resource?.type === "device" && row.resource?.id) {
    return `Device ${compactId(row.resource.id)}`;
  }
  return row.resource?.type || "-";
}

function eventTypeLabel(row) {
  if (row.device_type?.name) return row.device_type.name;
  if (row.related?.device_type_id) {
    return `Type ${compactId(row.related.device_type_id)}`;
  }
  return "-";
}

function targetLabel(endpoint) {
  if (!endpoint) return "-";
  if (endpoint.target_scope === "device") {
    const count = endpoint.target_device_ids?.length || 0;
    return `${count || 1} selected device${(count || 1) === 1 ? "" : "s"}`;
  }
  if (endpoint.target_scope === "device_type") {
    const count = endpoint.target_device_type_ids?.length || 0;
    return `${count || 1} selected device type${(count || 1) === 1 ? "" : "s"}`;
  }
  return "All organization devices";
}

function shortJson(value) {
  if (!value || (typeof value === "object" && !Object.keys(value).length)) {
    return "-";
  }
  const text = JSON.stringify(value);
  return text.length > 90 ? `${text.slice(0, 90)}...` : text;
}

const HEALTH_TONE = {
  healthy: { color: "#166534", bg: "rgba(22,163,74,0.14)" },
  degraded: { color: "#92400E", bg: "rgba(245,158,11,0.16)" },
  unhealthy: { color: "#B91C1C", bg: "rgba(239,68,68,0.14)" },
  pending: { color: "#1D4ED8", bg: "rgba(59,130,246,0.14)" },
  no_traffic: { color: "#475569", bg: "rgba(100,116,139,0.12)" },
  disabled: { color: "#475569", bg: "rgba(100,116,139,0.12)" },
};

function healthChip(health) {
  const key = health?.status || "no_traffic";
  const tone = HEALTH_TONE[key] || HEALTH_TONE.no_traffic;
  return (
    <Tooltip title={health?.reason || ""}>
      <Chip
        size="small"
        label={health?.label || "No traffic"}
        sx={{
          fontWeight: 700,
          bgcolor: tone.bg,
          color: tone.color,
          border: "1px solid rgba(148,163,184,0.22)",
        }}
      />
    </Tooltip>
  );
}

function MetricTile({ label, value, helper, tone = "default" }) {
  const colors = {
    default: "#0F172A",
    good: "#166534",
    warn: "#92400E",
    bad: "#B91C1C",
    info: "#075985",
  };
  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid rgba(148,163,184,0.22)",
        borderRadius: 2,
        bgcolor: "#FFFFFF",
        minHeight: 110,
      }}
    >
      <Typography variant="body2" sx={{ color: "#64748B", mb: 1 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: colors[tone] }}>
        {value}
      </Typography>
      {helper ? (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, color: "#64748B" }}
        >
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

function MiniBarChart({ title, data, series, color, emptyLabel = "No data" }) {
  const max = Math.max(
    1,
    ...(data || []).map((item) => Number(item?.[series] || 0)),
  );
  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid rgba(148,163,184,0.22)",
        borderRadius: 2,
        bgcolor: "#FFFFFF",
        minWidth: 0,
      }}
    >
      <Typography variant="body2" sx={{ color: "#64748B", mb: 1 }}>
        {title}
      </Typography>
      {(data || []).length ? (
        <Box
          sx={{
            height: 96,
            display: "flex",
            alignItems: "flex-end",
            gap: 0.5,
            overflow: "hidden",
          }}
        >
          {(data || []).map((item) => (
            <Tooltip
              key={`${series}-${item.time}`}
              title={`${formatDateTime(item.time)}: ${formatMetric(item?.[series])}`}
            >
              <Box
                sx={{
                  flex: "1 1 0",
                  minWidth: 4,
                  height: `${Math.max(4, (Number(item?.[series] || 0) / max) * 100)}%`,
                  bgcolor: color,
                  borderRadius: "4px 4px 0 0",
                }}
              />
            </Tooltip>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      )}
    </Box>
  );
}

function WebhooksTableSkeleton() {
  return (
    <Box sx={{ pt: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2 }}
        alignItems={{ sm: "center" }}
      >
        <Skeleton variant="rounded" width={260} height={40} />
        <Skeleton variant="rounded" width={160} height={40} />
        <Skeleton variant="text" width={180} height={24} />
      </Stack>
      <Box
        sx={{
          border: "1px solid rgba(148,163,184,0.2)",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "#FAFBFC",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid rgba(148,163,184,0.2)",
            bgcolor: "#F1F5F9",
          }}
        >
          {[120, 220, 100, 90, 100, 120].map((width) => (
            <Skeleton key={width} variant="text" width={width} height={24} />
          ))}
        </Stack>
        <Stack spacing={0} sx={{ p: 1.5 }}>
          {Array.from({ length: 7 }).map((_, index) => (
            <Stack
              key={index}
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ py: 1.25 }}
            >
              <Skeleton variant="text" width="18%" height={26} />
              <Skeleton variant="text" width="28%" height={26} />
              <Skeleton variant="rounded" width={54} height={24} />
              <Skeleton variant="text" width="12%" height={26} />
              <Skeleton variant="text" width="12%" height={26} />
              <Skeleton
                variant="rounded"
                width={86}
                height={30}
                sx={{ ml: "auto" }}
              />
            </Stack>
          ))}
        </Stack>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1.5 }}>
        <Skeleton variant="text" width={280} height={36} />
      </Box>
    </Box>
  );
}

export default function WebhooksPage() {
  const { activeOrg, permissions } = useAuth();
  const {
  notification,
  showNotification,
  closeNotification,
} = useNotification();
  const token = localStorage.getItem("authToken");

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  //const [success, setSuccess] = useState("");

  const [endpoints, setEndpoints] = useState([]);
  const [catalogEvents, setCatalogEvents] = useState([]);
  const [clientOrganizations, setClientOrganizations] = useState([]);
  const [targetDevices, setTargetDevices] = useState([]);
  const [targetDeviceTypes, setTargetDeviceTypes] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesTotal, setDeliveriesTotal] = useState(0);
  const [monitoring, setMonitoring] = useState(null);
  const [monitoringHours, setMonitoringHours] = useState(24);
  const [monitoringError, setMonitoringError] = useState(null);
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState("");
  const [apiKeys, setApiKeys] = useState([]);

  const [endpointDialogOpen, setEndpointDialogOpen] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState(null);
  const [savingEndpoint, setSavingEndpoint] = useState(false);
  const [deleteEndpointDialog, setDeleteEndpointDialog] = useState(null);
  const [deletingEndpoint, setDeletingEndpoint] = useState(false);

  const [secretDialog, setSecretDialog] = useState(null);
  const [apiKeyDialog, setApiKeyDialog] = useState(null);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyScopes, setNewKeyScopes] = useState(["webhook.manage"]);
  const [creatingKey, setCreatingKey] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);

  const handleOpenDetails = (row) => {
    setSelectedDelivery(row);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedDelivery(null);
  };

  const WEBHOOK_KEY_SCOPE_OPTIONS = [
    {
      value: "webhook.view",
      label: "View",
      description: "List endpoints, deliveries, and API keys",
    },
    {
      value: "webhook.manage",
      label: "Manage",
      description: "Create, update, delete, and test webhooks",
    },
  ];

  const [attempts, setAttempts] = useState([]);
  const [loadingAttempts, setLoadingAttempts] = useState(false);

  const [endpointSearch, setEndpointSearch] = useState("");
  const [deliveryEndpointFilter, setDeliveryEndpointFilter] = useState("");
  const [deliveryEventTypeFilter, setDeliveryEventTypeFilter] = useState("");
  const [deliveryDateFrom, setDeliveryDateFrom] = useState("");
  const [deliveryDateTo, setDeliveryDateTo] = useState("");
  const [endpointPage, setEndpointPage] = useState(0);
  const [endpointRowsPerPage, setEndpointRowsPerPage] = useState(10);
  const [deliveryPage, setDeliveryPage] = useState(0);
  const [deliveryRowsPerPage, setDeliveryRowsPerPage] = useState(10);
  const [apiKeyPage, setApiKeyPage] = useState(0);
  const [apiKeyRowsPerPage, setApiKeyRowsPerPage] = useState(10);
  const [testDialogEndpoint, setTestDialogEndpoint] = useState(null);
  const [testEventType, setTestEventType] = useState("device.created");
  const [testAttributesJson, setTestAttributesJson] = useState("{}");
  const [testingEndpoint, setTestingEndpoint] = useState(false);
  const [eventSearch, setEventSearch] = useState("");
  const [openDomains, setOpenDomains] = useState([]);

  const canView = can(permissions, "webhook.view");
  const canManage = can(permissions, "webhook.manage");

  const loadCatalog = useCallback(async () => {
    if (!token) return;
    const data = await getRequest(`${WEBHOOKS_API}/events`, token, activeOrg);
    setCatalogEvents(data?.data || []);
  }, [token, activeOrg]);

  const loadEndpoints = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    const data = await getRequest(
      `${WEBHOOKS_API}/endpoints`,
      token,
      activeOrg,
    );
    setEndpoints(data?.data || []);
  }, [token, activeOrg]);

  const loadClientOrganizations = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    const data = await getRequest(
      `${WEBHOOKS_API}/client-organizations`,
      token,
      activeOrg,
    );
    setClientOrganizations(data?.data || []);
  }, [token, activeOrg]);

  const loadWebhookTargets = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    const [devicesData, typesData] = await Promise.all([
      getRequest(`${WEBHOOKS_API}/target-devices`, token, activeOrg),
      getRequest(`${WEBHOOKS_API}/target-device-types`, token, activeOrg),
    ]);
    setTargetDevices(devicesData?.data || []);
    setTargetDeviceTypes(typesData?.data || []);
  }, [token, activeOrg]);

  const loadDeliveries = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    const params = new URLSearchParams({
      per_page: String(deliveryRowsPerPage),
      page: String(deliveryPage + 1),
    });
    if (deliveryStatusFilter) params.set("status", deliveryStatusFilter);
    if (deliveryEndpointFilter)
      params.set("endpoint_id", deliveryEndpointFilter);
    if (deliveryEventTypeFilter)
      params.set("event_type", deliveryEventTypeFilter);
    if (deliveryDateFrom) params.set("created_from", deliveryDateFrom);
    if (deliveryDateTo) params.set("created_to", deliveryDateTo);
    const data = await getRequest(
      `${WEBHOOKS_API}/deliveries?${params}`,
      token,
      activeOrg,
    );
    setDeliveries(data?.data || []);
    setDeliveriesTotal(data?.meta?.total || 0);
    return data;
  }, [
    token,
    activeOrg,
    deliveryStatusFilter,
    deliveryEndpointFilter,
    deliveryEventTypeFilter,
    deliveryDateFrom,
    deliveryDateTo,
    deliveryPage,
    deliveryRowsPerPage,
  ]);

  const loadMonitoring = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    const url = `${WEBHOOKS_API}/monitoring?hours=${monitoringHours}`;
    try {
      const data = await getRequest(url, token, activeOrg);
      setMonitoring(data || null);
      setMonitoringError(null);
      return data;
    } catch (err) {
      const status = err?.response?.status || 0;
      const method = err?.config?.method?.toUpperCase() || "GET";
      const requestUrl = err?.config?.url || url;
      const code =
        err?.response?.data?.code ||
        (status === 0 ? "NETWORK_OR_CORS" : `HTTP_${status}`);
      const message = getErrorMessage(
        err,
        status === 0
          ? "Monitoring request failed before the API response was readable"
          : "Monitoring request failed",
      );
      setMonitoringError({
        code,
        status,
        method,
        url: requestUrl,
        message,
      });
      return null;
    }
  }, [token, activeOrg, monitoringHours]);

  const loadApiKeys = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    const data = await getRequest(`${WEBHOOKS_API}/api-keys`, token, activeOrg);
    setApiKeys(data?.data || []);
  }, [token, activeOrg]);

  const refreshAll = useCallback(async () => {
    if (!token || !activeOrg?.id) return;
    setLoading(true);
    setError("");
    try {
      await Promise.all([
        loadCatalog(),
        loadEndpoints(),
        loadClientOrganizations(),
        loadWebhookTargets(),
        loadDeliveries(),
        loadApiKeys(),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load webhooks data"));
    } finally {
      setLoading(false);
    }
  }, [
    token,
    activeOrg,
    loadCatalog,
    loadEndpoints,
    loadClientOrganizations,
    loadWebhookTargets,
    loadDeliveries,
    loadApiKeys,
  ]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (tab === 1) loadDeliveries();
    if (tab === 2) loadMonitoring();
  }, [tab, loadDeliveries, loadMonitoring]);

  useEffect(() => {
    setDeliveryPage(0);
  }, [
    deliveryStatusFilter,
    deliveryEndpointFilter,
    deliveryEventTypeFilter,
    deliveryDateFrom,
    deliveryDateTo,
    deliveryRowsPerPage,
  ]);

  useEffect(() => {
    if (![1, 2].includes(tab) || !token || !activeOrg?.id) return undefined;
    const timer = window.setInterval(
      () => {
        if (tab === 1) loadDeliveries();
        if (tab === 2) loadMonitoring();
      },
      tab === 2 ? 60000 : 10000,
    );
    return () => window.clearInterval(timer);
  }, [tab, token, activeOrg, loadDeliveries, loadMonitoring]);

  const filteredEndpoints = React.useMemo(() => {
    const q = endpointSearch.trim().toLowerCase();
    if (!q) return endpoints;
    return endpoints.filter(
      (row) =>
        row.name?.toLowerCase().includes(q) ||
        row.url?.toLowerCase().includes(q) ||
        row.auth_type?.toLowerCase().includes(q) ||
        row.description?.toLowerCase().includes(q),
    );
  }, [endpoints, endpointSearch]);

  const paginatedEndpoints = React.useMemo(
    () =>
      filteredEndpoints.slice(
        endpointPage * endpointRowsPerPage,
        endpointPage * endpointRowsPerPage + endpointRowsPerPage,
      ),
    [filteredEndpoints, endpointPage, endpointRowsPerPage],
  );

  const paginatedApiKeys = React.useMemo(
    () =>
      apiKeys.slice(
        apiKeyPage * apiKeyRowsPerPage,
        apiKeyPage * apiKeyRowsPerPage + apiKeyRowsPerPage,
      ),
    [apiKeys, apiKeyPage, apiKeyRowsPerPage],
  );

  const filteredCatalogEvents = React.useMemo(() => {
    const q = eventSearch.trim().toLowerCase();
    if (!q) return catalogEvents;
    return catalogEvents.filter((event) => {
      const haystack = [event.type, event.domain, event.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [catalogEvents, eventSearch]);

  const catalogDomains = React.useMemo(
    () =>
      Array.from(
        new Set(
          (filteredCatalogEvents || [])
            .map((event) => event.domain)
            .filter(Boolean),
        ),
      ).sort(),
    [filteredCatalogEvents],
  );

  const eventsByDomain = React.useMemo(() => {
    const grouped = new Map();
    for (const event of filteredCatalogEvents) {
      const domain = event.domain || "other";
      if (!grouped.has(domain)) grouped.set(domain, []);
      grouped.get(domain).push(event);
    }
    return Array.from(grouped.entries())
      .map(([domain, events]) => ({
        domain,
        events: events.sort((a, b) =>
          String(a.type).localeCompare(String(b.type)),
        ),
      }))
      .sort((a, b) => a.domain.localeCompare(b.domain));
  }, [filteredCatalogEvents]);

  useEffect(() => {
    setEndpointPage(0);
  }, [endpointSearch, endpoints.length]);

  useEffect(() => {
    setDeliveryPage(0);
  }, [deliveryStatusFilter, deliveryEndpointFilter, deliveries.length]);

  useEffect(() => {
    setApiKeyPage(0);
  }, [apiKeys.length]);

  useEffect(() => {
    if (!filteredCatalogEvents.length) {
      setOpenDomains([]);
      return;
    }
    setOpenDomains((prev) => {
      const next = catalogDomains.filter((domain) => prev.includes(domain));
      return next.length ? next : catalogDomains.slice(0, 1);
    });
  }, [catalogDomains, filteredCatalogEvents.length]);

  const toggleDomain = (domain) => {
    setOpenDomains((prev) =>
      prev.includes(domain)
        ? prev.filter((item) => item !== domain)
        : [...prev, domain],
    );
  };

  const handleCreateEndpoint = () => {
    setEditingEndpoint(null);
    setEndpointDialogOpen(true);
  };

  const handleEditEndpoint = (row) => {
    setEditingEndpoint(row);
    setEndpointDialogOpen(true);
  };

  const handleSaveEndpoint = async (payload) => {
    setSavingEndpoint(true);
    setError("");
    try {
      if (editingEndpoint?.id) {
        const updated = await patchRequest(
          `${WEBHOOKS_API}/endpoints/${editingEndpoint.id}`,
          payload,
          token,
          activeOrg,
        );
        if (updated.signing_secret) {
          setSecretDialog({
            title: "Signing secret rotated",
            secret: updated.signing_secret,
          });
        }
        showNotification("Webhook endpoint updated", "success");
      } else {
        const created = await postRequest(
          `${WEBHOOKS_API}/endpoints`,
          payload,
          token,
          activeOrg,
        );
        if (created.signing_secret) {
          setSecretDialog({
            title: "Save your signing secret",
            secret: created.signing_secret,
          });
        }
        showNotification("Webhook endpoint created", "success");
      }
      setEndpointDialogOpen(false);
      await loadEndpoints();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to save endpoint"));
      throw err;
    } finally {
      setSavingEndpoint(false);
    }
  };

  const handleToggleEnabled = async (row) => {
    if (!canManage) return;
    try {
      await patchRequest(
        `${WEBHOOKS_API}/endpoints/${row.id}`,
        { enabled: !row.enabled },
        token,
        activeOrg,
      );
      showNotification(
  row.enabled ? "Endpoint disabled" : "Endpoint enabled",
  "success"
);
      await loadEndpoints();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to update endpoint"));
    }
  };

  const handleDeleteEndpoint = async (row) => {
    if (!canManage) return;
    setDeleteEndpointDialog(row);
  };

  const confirmDeleteEndpoint = async () => {
    if (!deleteEndpointDialog?.id || !canManage) return;
    setDeletingEndpoint(true);
    try {
      await deleteRequest(
        `${WEBHOOKS_API}/endpoints/${deleteEndpointDialog.id}`,
        token,
        activeOrg,
      );
      showNotification("Webhook endpoint deleted", "success");
      setDeleteEndpointDialog(null);
      await loadEndpoints();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to delete endpoint"));
    } finally {
      setDeletingEndpoint(false);
    }
  };

  const openTestDialog = (row) => {
    const first = row.subscribed_events?.[0] || "device.created";
    setTestDialogEndpoint(row);
    setTestEventType(first);
    setTestAttributesJson('{\n  "message": "Webhook test delivery"\n}');
  };

  const handleTestEndpoint = async () => {
    if (!testDialogEndpoint) return;
    let attributes = { message: "Webhook test delivery" };
    if (testAttributesJson.trim()) {
      try {
        attributes = JSON.parse(testAttributesJson);
      } catch {
        setError("Test attributes must be valid JSON");
        return;
      }
    }
    setTestingEndpoint(true);
    try {
      await postRequest(
        `${WEBHOOKS_API}/endpoints/${testDialogEndpoint.id}/test`,
        { event_type: testEventType, attributes },
        token,
        activeOrg,
      );
      showNotification(
  "Test delivery queued — check Delivery log",
  "success"
);
      setTestDialogEndpoint(null);
      setTab(1);
      await loadDeliveries();
    } catch (err) {
      setError(getErrorMessage(err, "Test delivery failed"));
    } finally {
      setTestingEndpoint(false);
    }
  };

  const loadDeliveryAttempts = async (delivery) => {
    if (!delivery?.id) return;
    setLoadingAttempts(true);
    try {
      const data = await getRequest(
        `${WEBHOOKS_API}/deliveries/${delivery.id}/attempts`,
        token,
        activeOrg,
      );
      setAttempts(data?.data || []);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load delivery attempts"));
      setAttempts([]);
    } finally {
      setLoadingAttempts(false);
    }
  };

  const handleOpenDeliveryDetails = async (delivery) => {
    handleOpenDetails(delivery);
    setAttempts([]);
    await loadDeliveryAttempts(delivery);
  };

  const handleViewAttempts = async (delivery) => {
    await handleOpenDeliveryDetails(delivery);
  };

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      setError("API key name is required");
      return;
    }
    if (!newKeyScopes.length) {
      setError("Select at least one scope for the API key");
      return;
    }
    setCreatingKey(true);
    try {
      const data = await postRequest(
        `${WEBHOOKS_API}/api-keys`,
        { name: newKeyName.trim(), scopes: newKeyScopes },
        token,
        activeOrg,
      );
      setApiKeyDialog({
        name: data.name,
        api_key: data.api_key,
        scopes: data.scopes || newKeyScopes,
      });
      setNewKeyName("");
      setNewKeyScopes(["webhook.manage"]);
      await loadApiKeys();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to create API key"));
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeKey = async (row) => {
    if (!window.confirm(`Revoke API key "${row.name}"?`)) return;
    try {
      await deleteRequest(
        `${WEBHOOKS_API}/api-keys/${row.id}`,
        token,
        activeOrg,
      );
      showNotification("API key revoked", "success");
      await loadApiKeys();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to revoke API key"));
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification("Copied to clipboard", "success");
    } catch {
      setError("Could not copy to clipboard");
    }
  };

  if (!canView) {
    return (
      <Alert severity="warning">
        You do not have permission to view webhooks (webhook.view).
      </Alert>
    );
  }

  if (!activeOrg?.id) {
    return (
      <Alert severity="info">Select an organization to manage webhooks.</Alert>
    );
  }
  const DetailItem = ({ label, value }) => (
    <Box>
      <Typography
        variant="caption"
        sx={{
          color: "#64748B",
          fontWeight: 700,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>

      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {value || "—"}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", width: "100%" }}>
      <Box
        sx={{
          width: "100%",
          mb: 3,
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(255,255,255,0.14)",
                color: "#FFFFFF",
              }}
            >
              <WebhookRoundedIcon />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#FFFFFF", sx: 28, md: 40 }}
              >
                Webhooks
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.78)", mt: 0.5 }}
              >
                Configure outbound HTTPS notifications with custom Jinja2
                payloads for your integrations.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={refreshAll}
              disabled={loading}
              sx={{ color: "#FFFFFF", fontWeight: 700 }}
            >
              Refresh
            </Button>
            {canManage && tab === 0 ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateEndpoint}
                sx={{
                  px: 2.2,

                  py: 1.1,

                  borderRadius: 999,

                  textTransform: "none",

                  fontWeight: 700,

                  color: "#082F49",

                  background: "linear-gradient(135deg, #FDE68A, #F59E0B)",

                  boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                }}
              >
                New endpoint
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
      <Paper sx={{ ...panelSx, px: { xs: 1, sm: 2 }, pt: 1 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="none"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: "divider",

            "& .MuiTabs-flexContainer": {
              gap: 1,
            },

            "& .MuiTab-root": {
              minHeight: 48,
              whiteSpace: "nowrap",
            },

            "& .MuiTabs-scrollButtons": {
              display: {
                xs: "flex",
                md: "none",
              },
            },
          }}
        >
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Endpoints</span>

                {endpoints.length > 0 && (
                  <Chip
                    label={endpoints.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 11,
                    }}
                  />
                )}
              </Stack>
            }
          />

          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Delivery log</span>

                {deliveriesTotal > 0 && (
                  <Chip
                    label={deliveriesTotal}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 11,
                    }}
                  />
                )}
              </Stack>
            }
          />

          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Monitoring</span>
                {monitoring?.summary?.total > 0 && (
                  <Chip
                    label={monitoring.summary.total}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 11,
                    }}
                  />
                )}
              </Stack>
            }
          />

          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>API keys</span>

                {apiKeys.length > 0 && (
                  <Chip
                    label={apiKeys.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 11,
                    }}
                  />
                )}
              </Stack>
            }
          />

          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Event catalog</span>

                {catalogEvents.length > 0 && (
                  <Chip
                    label={catalogEvents.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 11,
                    }}
                  />
                )}
              </Stack>
            }
          />
        </Tabs>

        {loading ? (
          <WebhooksTableSkeleton />
        ) : (
          <>
            <TabPanel value={tab} index={0}>
              {endpoints.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    No webhook endpoints yet. Create one to receive platform
                    events at your URL.
                  </Typography>
                  {canManage ? (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleCreateEndpoint}
                    >
                      Create endpoint
                    </Button>
                  ) : null}
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      background:
                        "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                      p: 3,
                      mb: 2,
                      borderRadius: 3,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "#fff",
                          fontWeight: 800,
                        }}
                      >
                        Webhook Endpoints
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(255,255,255,0.75)",
                        }}
                      >
                        Search and manage your webhook endpoints.
                      </Typography>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Search by name, URL, or auth…"
                      value={endpointSearch}
                      onChange={(e) => setEndpointSearch(e.target.value)}
                      sx={{
                        width: 350,

                        "& .MuiOutlinedInput-root": {
                          color: "#fff",
                          borderRadius: 2,
                          background: "rgba(255,255,255,0.05)",

                          "& fieldset": {
                            borderColor: "rgba(255,255,255,0.2)",
                          },

                          "&:hover fieldset": {
                            borderColor: "rgba(255,255,255,0.4)",
                          },

                          "&.Mui-focused fieldset": {
                            borderColor: "#fff",
                          },
                        },

                        "& .MuiInputBase-input::placeholder": {
                          color: "rgba(255,255,255,0.7)",
                          opacity: 1,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              fontSize="small"
                              sx={{ color: "rgba(255,255,255,0.7)" }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: endpointSearch ? (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="clear search"
                              onClick={() => setEndpointSearch("")}
                              edge="end"
                              size="small"
                            >
                              <ClearIcon
                                fontSize="small"
                                sx={{ color: "rgba(255,255,255,0.7)" }}
                              />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  </Box>
                  {filteredEndpoints.length === 0 ? (
                    <Typography
                      color="text.secondary"
                      sx={{ py: 4, textAlign: "center" }}
                    >
                      No endpoints match your search.
                    </Typography>
                  ) : (
                    <>
                      <TableContainer sx={scrollableTableContainerSx}>
                        <Table size="small" stickyHeader sx={stickyTableHeadSx}>
                          <TableHead>
                            <TableRow>
                              <TableCell>S.No</TableCell>
                              <TableCell>Name</TableCell>
                              <TableCell>Notes</TableCell>
                              <TableCell>URL</TableCell>
                              <TableCell>Events</TableCell>
                              <TableCell>Client org</TableCell>
                              <TableCell>Target</TableCell>
                              <TableCell>Mode</TableCell>
                              <TableCell>Auth</TableCell>
                              <TableCell>PII</TableCell>
                              <TableCell>Rate/min</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedEndpoints.map((row, index) => (
                              <TableRow key={row.id} hover>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>
                                  {row.name}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 220 }}>
                                  <Tooltip title={row.description || ""}>
                                    <Typography variant="body2" noWrap>
                                      {row.description || "—"}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 200 }}>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.25}
                                  >
                                    <Tooltip title={row.url}>
                                      <Typography
                                        variant="body2"
                                        noWrap
                                        sx={{ maxWidth: 160, fontSize: 12 }}
                                      >
                                        {row.url}
                                      </Typography>
                                    </Tooltip>
                                    <Tooltip title="Copy URL">
                                      <IconButton
                                        size="small"
                                        onClick={() => copyText(row.url)}
                                        sx={{ p: 0.25 }}
                                      >
                                        <ContentCopyIcon
                                          sx={{ fontSize: 14 }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  {row.subscribed_events?.length || 0}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12 }}>
                                  {row.client_organization?.name || "None"}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12 }}>
                                  {row.target_scope === "device"
                                    ? `${row.target_device_ids?.length || 1} device${(row.target_device_ids?.length || 1) ===
                                      1
                                      ? ""
                                      : "s"
                                    }`
                                    : row.target_scope === "device_type"
                                      ? `${row.target_device_type_ids?.length || 1} device type${(row.target_device_type_ids?.length ||
                                        1) === 1
                                        ? ""
                                        : "s"
                                      }`
                                      : "All org devices"}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12 }}>
                                  {row.telemetry_delivery_mode || "real_time"}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12 }}>
                                  {row.auth_type}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12 }}>
                                  {row.pii_profile || "minimal"}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12 }}>
                                  {row.rate_limit_per_minute ?? "—"}
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 800,
                                      color: row.enabled
                                        ? "#166534"
                                        : "#64748B",
                                    }}
                                  >
                                    {row.enabled ? "Enabled" : "Disabled"}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Stack
                                    direction="row"
                                    spacing={0.5}
                                    justifyContent="flex-end"
                                  >
                                    {canManage ? (
                                      <>
                                        <Tooltip title="Send test">
                                          <IconButton
                                            size="small"
                                            onClick={() => openTestDialog(row)}
                                          >
                                            <PlayArrowIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleEditEndpoint(row)
                                            }
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip
                                          title={
                                            row.enabled ? "Disable" : "Enable"
                                          }
                                        >
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleToggleEnabled(row)
                                            }
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{ fontWeight: 800, px: 0.5 }}
                                            >
                                              {row.enabled ? "Off" : "On"}
                                            </Typography>
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                              handleDeleteEndpoint(row)
                                            }
                                          >
                                            <DeleteOutlineIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    ) : null}
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={filteredEndpoints.length}
                        page={endpointPage}
                        rowsPerPage={endpointRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        onPageChange={(_, newPage) => setEndpointPage(newPage)}
                        onRowsPerPageChange={(e) => {
                          setEndpointRowsPerPage(parseInt(e.target.value, 10));
                          setEndpointPage(0);
                        }}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      />
                    </>
                  )}
                </>
              )}
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                  p: 3,
                  mb: 2,
                  borderRadius: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    mb: 0.5,
                  }}
                >
                  Delivery History
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.75)",
                    mb: 3,
                  }}
                >
                  Monitor webhook deliveries, filter events, and inspect
                  failures.
                </Typography>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  flexWrap={{ xs: "nowrap", md: "wrap" }}
                  alignItems={{ xs: "stretch", md: "center" }}
                  sx={{
                    width: "100%",
                    gap: 1,

                    "& .MuiFormControl-root": {
                      width: { xs: "100%", md: "auto" },
                    },

                    "& .MuiTextField-root": {
                      width: { xs: "100%", md: "220px" },
                    },

                    "& .MuiButton-root": {
                      width: { xs: "100%", sm: "auto" },
                      minHeight: 40,
                    },

                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      color: "#fff",
                      background: "rgba(255,255,255,0.05)",

                      "& fieldset": {
                        borderColor: "rgba(255,255,255,0.2)",
                      },

                      "&:hover fieldset": {
                        borderColor: "rgba(255,255,255,0.35)",
                      },

                      "&.Mui-focused fieldset": {
                        borderColor: "#fff",
                      },
                    },

                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.7)",
                    },

                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#fff",
                    },

                    "& .MuiSelect-icon": {
                      color: "#fff",
                    },

                    "& .MuiInputBase-input": {
                      color: "#fff",
                    },

                    "& .MuiButton-outlined": {
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.3)",

                      "&:hover": {
                        borderColor: "#fff",
                        background: "rgba(255,255,255,0.08)",
                      },
                    },

                    "& .MuiButton-text": {
                      color: "#fff",
                    },

                    "& .MuiTypography-root": {
                      color: "rgba(255,255,255,0.8)",
                      width: { xs: "100%", md: "auto" },
                      textAlign: { xs: "center", md: "left" },
                    },
                    "& input::-webkit-calendar-picker-indicator": {
                      filter: "invert(1)",
                      cursor: "pointer",
                    },
                  }}
                >
                  <FormControl
                    size="small"
                    sx={{
                      minWidth: { xs: "100%", md: 100 },
                    }}
                    fullWidth
                  >
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={deliveryStatusFilter}
                      onChange={(e) => setDeliveryStatusFilter(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="delivered">Delivered</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{
                      minWidth: { xs: "100%", md: 110 },
                    }}
                  >
                    <InputLabel>Endpoint</InputLabel>
                    <Select
                      label="Endpoint"
                      value={deliveryEndpointFilter}
                      onChange={(e) => setDeliveryEndpointFilter(e.target.value)}
                    >
                      <MenuItem value="">All endpoints</MenuItem>
                      {endpoints.map((ep) => (
                        <MenuItem key={ep.id} value={ep.id}>
                          {ep.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    size="small"
                    sx={{
                      minWidth: { xs: "100%", md: 120 },
                    }}
                  >
                    <InputLabel>Event type</InputLabel>
                    <Select
                      label="Event type"
                      value={deliveryEventTypeFilter}
                      onChange={(e) => setDeliveryEventTypeFilter(e.target.value)}
                    >
                      <MenuItem value="">All event types</MenuItem>
                      {Array.from(
                        new Set(catalogEvents.map((event) => event.type))
                      ).map((eventType) => (
                        <MenuItem key={eventType} value={eventType}>
                          {eventType}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label="From"
                    value={deliveryDateFrom}
                    onChange={(e) => setDeliveryDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label="To"
                    value={deliveryDateTo}
                    onChange={(e) => setDeliveryDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />

                  {/* <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadDeliveries}
                    sx={{
                      minWidth: { xs: "100%", md: 200 },
                    }}
                  >
                    Refresh
                  </Button> */}

                  <Button
                    size="small"
                    onClick={() => {
                      setDeliveryStatusFilter("");
                      setDeliveryEndpointFilter("");
                      setDeliveryEventTypeFilter("");
                      setDeliveryDateFrom("");
                      setDeliveryDateTo("");
                    }}
                    sx={{
                      minWidth: { xs: "100%", md: 50 },
                    }}
                  >
                    Clear Filters
                  </Button>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      minWidth: { xs: "100%", md: "auto" },
                      textAlign: { xs: "center", md: "left" },
                    }}
                  >
                    Showing {deliveries.length} of {deliveriesTotal} deliveries
                  </Typography>
                </Stack>
              </Box>
              {deliveries.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    No deliveries yet. Send a test from an endpoint or trigger a
                    subscribed event.
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer sx={scrollableTableContainerSx}>
                    <Table size="small" stickyHeader sx={stickyTableHeadSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>S.No</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Event</TableCell>
                          <TableCell>Published</TableCell>
                          <TableCell>Device</TableCell>
                          <TableCell>Device type</TableCell>
                          <TableCell>Endpoint</TableCell>
                          <TableCell>HTTP</TableCell>
                          <TableCell>Attempts</TableCell>
                          <TableCell>Error</TableCell>
                          <TableCell align="right">Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {deliveries.map((row, index) => (
                          <TableRow key={row.id} hover>
                            <TableCell>
                              {deliveryPage * deliveryRowsPerPage + index + 1}
                            </TableCell>
                            <TableCell>
                              {deliveryStatusChip(row.status)}
                            </TableCell>
                            <TableCell
                              sx={{ fontFamily: "monospace", fontSize: 12 }}
                            >
                              {row.event_type}
                            </TableCell>
                            <TableCell sx={{ minWidth: 160 }}>
                              <Typography variant="caption">
                                {formatDateTime(row.event_occurred_at)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                queued {formatDateTime(row.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: 150 }}>
                              <Typography variant="body2" noWrap>
                                {eventDeviceLabel(row)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                {compactId(row.resource?.id)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: 140 }}>
                              {eventTypeLabel(row)}
                            </TableCell>
                            <TableCell>{row.endpoint?.name}</TableCell>
                            <TableCell>{row.last_http_status ?? "—"}</TableCell>
                            <TableCell>{row.attempt_count}</TableCell>
                            <TableCell sx={{ maxWidth: 180 }}>
                              {row.last_error ? (
                                <Tooltip title={row.last_error}>
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    noWrap
                                    sx={{ display: "block", maxWidth: 160 }}
                                  >
                                    {row.last_error}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="flex-end"
                              >
                                <Button
                                  size="small"
                                  // variant="outlined"
                                  onClick={() => handleOpenDeliveryDetails(row)}
                                >
                                  Details
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => handleOpenDeliveryDetails(row)}
                                >
                                  Attempts
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={deliveriesTotal}
                    page={deliveryPage}
                    rowsPerPage={deliveryRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    onPageChange={(_, newPage) => setDeliveryPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setDeliveryRowsPerPage(parseInt(e.target.value, 10));
                      setDeliveryPage(0);
                    }}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  />
                </>
              )}
            </TabPanel>

            <TabPanel value={tab} index={2}>
              {monitoringError ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Monitoring failed: {monitoringError.message}{" "}
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ display: "block", mt: 0.5, fontFamily: "monospace" }}
                  >
                    code={monitoringError.code}; status=
                    {monitoringError.status || "no-response"}; method=
                    {monitoringError.method}; url={monitoringError.url}
                  </Typography>
                </Alert>
              ) : null}
              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                  p: 3,
                  borderRadius: 3,
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "#fff",
                    fontWeight: 800,
                    mb: 0.5,
                  }}
                >
                  Monitoring Dashboard
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.75)",
                    mb: 3,
                  }}
                >
                  Monitor delivery performance, latency, endpoint health, and
                  webhook activity.
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      color: "#fff",
                      background: "rgba(255,255,255,0.05)",

                      "& fieldset": {
                        borderColor: "rgba(255,255,255,0.2)",
                      },

                      "&:hover fieldset": {
                        borderColor: "rgba(255,255,255,0.35)",
                      },

                      "&.Mui-focused fieldset": {
                        borderColor: "#fff",
                      },
                    },

                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.7)",
                    },

                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#fff",
                    },

                    "& .MuiSelect-icon": {
                      color: "#fff",
                    },

                    "& .MuiButton-outlined": {
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.3)",

                      "&:hover": {
                        borderColor: "#fff",
                        background: "rgba(255,255,255,0.08)",
                      },
                    },

                    "& .MuiTypography-root": {
                      color: "rgba(255,255,255,0.8)",
                    },
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 150 } }}>
                    <InputLabel>Date range</InputLabel>
                    <Select
                      label="Date range"
                      value={monitoringHours}
                      onChange={(event) =>
                        setMonitoringHours(Number(event.target.value))
                      }
                    >
                      <MenuItem value={1}>Last 1 hour</MenuItem>
                      <MenuItem value={24}>Last 24 hours</MenuItem>
                      <MenuItem value={168}>Last 7 days</MenuItem>
                    </Select>
                  </FormControl>
                  {/* <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadMonitoring}
                    sx={{ fontWeight: 700 }}
                  >
                    Refresh
                  </Button> */}
                  <Typography variant="body2" color="text.secondary">
                    Auto-refreshes while this tab is open.
                  </Typography>
                </Stack>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <MetricTile
                  label="Total deliveries"
                  value={formatMetric(monitoring?.summary?.total)}
                  helper={`${formatMetric(monitoring?.summary?.pending)} pending`}
                  tone="info"
                />
                <MetricTile
                  label="Success rate"
                  value={formatMetric(monitoring?.summary?.success_rate, "%")}
                  helper={`${formatMetric(monitoring?.summary?.delivered)} delivered`}
                  tone={
                    (monitoring?.summary?.success_rate || 0) >= 95
                      ? "good"
                      : "warn"
                  }
                />
                <MetricTile
                  label="HTTP errors"
                  value={formatMetric(monitoring?.summary?.http_errors)}
                  helper={`${formatMetric(monitoring?.summary?.failed)} failed deliveries`}
                  tone={monitoring?.summary?.http_errors ? "bad" : "good"}
                />
                <MetricTile
                  label="Latency"
                  value={formatMs(monitoring?.summary?.avg_attempt_duration_ms)}
                  helper={`p95 ${formatMs(monitoring?.summary?.p95_attempt_duration_ms)}`}
                  tone="default"
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <MiniBarChart
                  title="Deliveries over time"
                  data={monitoring?.timeline || []}
                  series="total"
                  color="#0284C7"
                />
                <MiniBarChart
                  title="Successful deliveries"
                  data={monitoring?.timeline || []}
                  series="delivered"
                  color="#16A34A"
                />
                <MiniBarChart
                  title="Failed deliveries"
                  data={monitoring?.timeline || []}
                  series="failed"
                  color="#DC2626"
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <MetricTile
                  label="Delivery latency"
                  value={formatMs(monitoring?.summary?.avg_delivery_latency_ms)}
                  helper={`p95 ${formatMs(monitoring?.summary?.p95_delivery_latency_ms)}`}
                  tone="default"
                />
                <MetricTile
                  label="Retries"
                  value={formatMetric(monitoring?.summary?.retrying)}
                  helper="Deliveries needing more than one attempt"
                  tone={monitoring?.summary?.retrying ? "warn" : "good"}
                />
                <MetricTile
                  label="Failed"
                  value={formatMetric(monitoring?.summary?.failed)}
                  helper={`${formatMetric(monitoring?.summary?.failure_rate, "%")} failure rate`}
                  tone={monitoring?.summary?.failed ? "bad" : "good"}
                />
                <MetricTile
                  label="Cancelled"
                  value={formatMetric(monitoring?.summary?.cancelled)}
                  helper={`${formatMetric(monitoring?.summary?.processing)} processing`}
                  tone={monitoring?.summary?.cancelled ? "warn" : "default"}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    lg: "minmax(0, 2fr) minmax(280px, 0.65fr)",
                  },
                  gap: 2,
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800, mb: 1 }}
                  >
                    Endpoint health status
                  </Typography>
                  <TableContainer
                    sx={{
                      ...scrollableTableContainerSx,
                      maxWidth: "100%",
                    }}
                  >
                    <Table size="small" stickyHeader sx={stickyTableHeadSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Endpoint</TableCell>
                          <TableCell>Health</TableCell>
                          <TableCell>Success</TableCell>
                          <TableCell>Pending</TableCell>
                          <TableCell>Retries</TableCell>
                          <TableCell>HTTP errors</TableCell>
                          <TableCell>Avg latency</TableCell>
                          <TableCell>Delivery latency</TableCell>
                          <TableCell>Last HTTP</TableCell>
                          <TableCell>Last delivery</TableCell>
                          <TableCell>Last success</TableCell>
                          <TableCell>Last failure</TableCell>
                          <TableCell>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(monitoring?.endpoints || []).map((row) => (
                          <TableRow key={row.endpoint.id} hover>
                            <TableCell sx={{ minWidth: 180 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                              >
                                {row.endpoint.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {row.endpoint.telemetry_delivery_mode ||
                                  "real_time"}{" "}
                                - {row.endpoint.auth_type}
                              </Typography>
                            </TableCell>
                            <TableCell>{healthChip(row.health)}</TableCell>
                            <TableCell>
                              {formatMetric(row.metrics.success_rate, "%")}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                {formatMetric(row.metrics.delivered)} /{" "}
                                {formatMetric(row.metrics.total)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {formatMetric(row.metrics.pending)}
                            </TableCell>
                            <TableCell>
                              {formatMetric(row.metrics.retrying)}
                            </TableCell>
                            <TableCell>
                              {formatMetric(row.metrics.http_errors)}
                            </TableCell>
                            <TableCell>
                              {formatMs(row.metrics.avg_attempt_duration_ms)}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                p95{" "}
                                {formatMs(row.metrics.p95_attempt_duration_ms)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {formatMs(row.metrics.avg_delivery_latency_ms)}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                p95{" "}
                                {formatMs(row.metrics.p95_delivery_latency_ms)}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.last.http_status ?? "-"}</TableCell>
                            <TableCell sx={{ minWidth: 150 }}>
                              {formatDateTime(row.last.delivery_at)}
                            </TableCell>
                            <TableCell sx={{ minWidth: 150 }}>
                              {formatDateTime(row.last.success_at)}
                            </TableCell>
                            <TableCell sx={{ minWidth: 150 }}>
                              {formatDateTime(row.last.failure_at)}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 220 }}>
                              {row.last.error ? (
                                <Tooltip title={row.last.error}>
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    noWrap
                                    sx={{ display: "block" }}
                                  >
                                    {row.last.error}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800, mb: 1 }}
                  >
                    Event delivery metrics
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid rgba(148,163,184,0.2)",
                      borderRadius: 2,
                      overflow: "hidden",
                      bgcolor: "#FFFFFF",
                      maxWidth: "100%",
                    }}
                  >
                    {(monitoring?.event_breakdown || []).length ? (
                      (monitoring?.event_breakdown || []).map((item) => (
                        <Box
                          key={item.event_type}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                            px: 2,
                            py: 1.5,
                            borderBottom: "1px solid rgba(148,163,184,0.16)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.event_type}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {formatMetric(item.count)}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ p: 2 }}
                      >
                        No deliveries in this window.
                      </Typography>
                    )}
                  </Box>

                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800, mt: 2, mb: 1 }}
                  >
                    Delivery state
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid rgba(148,163,184,0.2)",
                      borderRadius: 2,
                      overflow: "hidden",
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    {[
                      ["Delivered", monitoring?.summary?.delivered, "good"],
                      ["Pending", monitoring?.summary?.pending, "info"],
                      ["Processing", monitoring?.summary?.processing, "info"],
                      ["Failed", monitoring?.summary?.failed, "bad"],
                      ["Cancelled", monitoring?.summary?.cancelled, "warn"],
                    ].map(([label, value, tone]) => {
                      const colors = {
                        good: "#166534",
                        info: "#075985",
                        warn: "#92400E",
                        bad: "#B91C1C",
                      };
                      return (
                        <Box
                          key={label}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                            px: 2,
                            py: 1.3,
                            borderBottom: "1px solid rgba(148,163,184,0.16)",
                          }}
                        >
                          <Typography variant="body2">{label}</Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 800, color: colors[tone] }}
                          >
                            {formatMetric(value)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </TabPanel>

            <TabPanel value={tab} index={3}>
              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                  p: 3,
                  borderRadius: 3,
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: "#fff",
                    mb: 1,
                  }}
                >
                  API Keys
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.75)",
                    mb: 3,
                  }}
                >
                  API keys (prefix <code>whk_</code>) for automation. Keys are
                  shown once at creation. Choose scopes to limit what each key
                  can do.
                </Typography>

                {canManage ? (
                  <Stack
                    spacing={2}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        color: "#fff",
                        background: "rgba(255,255,255,0.05)",

                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.2)",
                        },

                        "&:hover fieldset": {
                          borderColor: "rgba(255,255,255,0.35)",
                        },

                        "&.Mui-focused fieldset": {
                          borderColor: "#fff",
                        },
                      },

                      "& .MuiInputLabel-root": {
                        color: "rgba(255,255,255,0.7)",
                      },

                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#fff",
                      },

                      "& .MuiSelect-icon": {
                        color: "#fff",
                      },

                      "& .MuiInputBase-input::placeholder": {
                        color: "rgba(255,255,255,0.7)",
                        opacity: 1,
                      },
                    }}
                  >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <TextField
                        size="small"
                        label="Key name"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <FormControl
                        size="small"
                        sx={{ minWidth: { xs: "100%", sm: 260 } }}
                      >
                        <InputLabel>Scopes</InputLabel>
                        <Select
                          multiple
                          label="Scopes"
                          value={newKeyScopes}
                          onChange={(e) => {
                            const value = e.target.value;
                            setNewKeyScopes(
                              typeof value === "string"
                                ? value.split(",")
                                : value,
                            );
                          }}
                          renderValue={(selected) =>
                            (selected.length
                              ? selected
                              : ["webhook.manage"]
                            ).join(", ")
                          }
                        >
                          {WEBHOOK_KEY_SCOPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {opt.label}{" "}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ({opt.value})
                                  </Typography>
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {opt.description}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        startIcon={<VpnKeyIcon />}
                        onClick={handleCreateApiKey}
                        disabled={creatingKey}
                        sx={{
                          alignSelf: { sm: "center" },
                          flexShrink: 0,
                          height: 40,
                          borderRadius: 2,
                          background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                          boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                          color: "#0d0d0d",
                        }}
                      >
                        {creatingKey ? "Creating…" : "Create key"}
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="#fff">
                      <strong>Manage</strong> includes view access. Use{" "}
                      <strong>View</strong> only for read-only monitoring
                      scripts.
                    </Typography>
                  </Stack>
                ) : null}
              </Box>
              {apiKeys.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    No API keys yet. Create one to automate webhook management
                    via the API.
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer sx={scrollableTableContainerSx}>
                    <Table size="small" stickyHeader sx={stickyTableHeadSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Scopes</TableCell>
                          <TableCell>Prefix</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedApiKeys.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.name}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>
                              {(row.scopes || []).join(", ") || "—"}
                            </TableCell>
                            <TableCell sx={{ fontFamily: "monospace" }}>
                              {row.key_prefix}…
                            </TableCell>
                            <TableCell>
                              {row.created_at
                                ? new Date(row.created_at).toLocaleString()
                                : "—"}
                            </TableCell>
                            <TableCell align="right">
                              {canManage ? (
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleRevokeKey(row)}
                                >
                                  Revoke
                                </Button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={apiKeys.length}
                    page={apiKeyPage}
                    rowsPerPage={apiKeyRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    onPageChange={(_, newPage) => setApiKeyPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setApiKeyRowsPerPage(parseInt(e.target.value, 10));
                      setApiKeyPage(0);
                    }}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  />
                </>
              )}
            </TabPanel>

            <TabPanel value={tab} index={4}>
              <Stack spacing={2}>
                <Box
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
                    p: 3,
                    borderRadius: 3,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      mb: 0.5,
                      color: "#fff",
                    }}
                  >
                    Event catalog
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.75)",
                      mb: 3,
                    }}
                  >
                    Browse the supported webhook event types, search by event
                    name or description, and quickly spot high-volume events
                    that may need rate limits or batching.
                  </Typography>

                  <Stack
                    direction={{ xs: "column", lg: "row" }}
                    spacing={2}
                    flexWrap="wrap"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        color: "#fff",
                        background: "rgba(255,255,255,0.05)",

                        "& fieldset": {
                          borderColor: "rgba(255,255,255,0.15)",
                        },

                        "&:hover fieldset": {
                          borderColor: "rgba(255,255,255,0.3)",
                        },

                        "&.Mui-focused fieldset": {
                          borderColor: "#fff",
                        },
                      },

                      "& .MuiInputLabel-root": {
                        color: "rgba(255,255,255,0.7)",
                      },

                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#fff",
                      },

                      "& .MuiInputBase-input::placeholder": {
                        color: "rgba(255,255,255,0.7)",
                        opacity: 1,
                      },
                    }}
                  >
                    <TextField
                      size="small"
                      label="Search events"
                      placeholder="Search by event, domain..."
                      value={eventSearch}
                      onChange={(e) => setEventSearch(e.target.value)}
                      sx={{
                        width: { xs: "100%", md: "300px" },
                        flexShrink: 0,
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              fontSize="small"
                              sx={{ color: "rgba(255,255,255,0.7)" }}
                            />
                          </InputAdornment>
                        ),

                        endAdornment: eventSearch ? (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="clear event search"
                              onClick={() => setEventSearch("")}
                              edge="end"
                              size="small"
                            >
                              <ClearIcon
                                fontSize="small"
                                sx={{ color: "rgba(255,255,255,0.7)" }}
                              />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />

                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ columnGap: 1, rowGap: 1.5 }}>
                      <Chip
                        label={`${catalogEvents.length} total events`}
                        variant="outlined"
                        sx={{
                          color: "#fff",
                          borderColor: "rgba(255,255,255,0.3)",
                        }}
                      />

                      <Chip
                        label={`${filteredCatalogEvents.filter(
                          (event) => event.high_volume,
                        ).length
                          } high-volume`}
                        variant="outlined"
                        sx={{
                          color: "#fff",
                          borderColor: "#f59e0b",
                        }}
                      />

                      <Chip
                        label={`${catalogDomains.length} domains`}
                        variant="outlined"
                        sx={{
                          color: "#fff",
                          borderColor: "rgba(255,255,255,0.3)",
                        }}
                      />
                    </Stack>
                  </Stack>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {/* {catalogDomains.map((domain) => (
                    <Chip
                      key={domain}
                      label={domain}
                      variant="outlined"
                      size="small"
                      sx={{ textTransform: "capitalize" }}
                    />
                  ))} */}
                </Box>

                {filteredCatalogEvents.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No events match your search.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1.25}>
                    {eventsByDomain.map(({ domain, events }) => {
                      const expanded = openDomains.includes(domain);
                      const highVolumeCount = events.filter(
                        (event) => event.high_volume,
                      ).length;
                      return (
                        <Accordion
                          key={domain}
                          expanded={expanded}
                          onChange={() => toggleDomain(domain)}
                          disableGutters
                          sx={{
                            border: "1px solid rgba(148,163,184,0.2)",
                            borderRadius: 2,
                            overflow: "hidden",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              px: 2,
                              bgcolor: "rgba(248,250,252,0.9)",
                              "& .MuiAccordionSummary-content": {
                                alignItems: "center",
                              },
                            }}
                          >
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              alignItems={{ sm: "center" }}
                              sx={{ width: "100%" }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 800,
                                  textTransform: "capitalize",
                                }}
                              >
                                {domain}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                              >
                                <Chip
                                  label={`${events.length} events`}
                                  size="small"
                                  variant="outlined"
                                />
                                {highVolumeCount > 0 ? (
                                  <Chip
                                    label={`${highVolumeCount} high-volume`}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                  />
                                ) : null}
                              </Stack>
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 0 }}>
                            <TableContainer sx={scrollableTableContainerSx}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Event</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Volume</TableCell>
                                    <TableCell align="right">Copy</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {events.map((event) => (
                                    <TableRow key={event.type} hover>
                                      <TableCell sx={{ fontWeight: 700 }}>
                                        <Stack spacing={0.5}>
                                          <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 800 }}
                                          >
                                            {event.type}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Subscribable webhook event
                                          </Typography>
                                        </Stack>
                                      </TableCell>
                                      <TableCell sx={{ maxWidth: 520 }}>
                                        <Typography variant="body2">
                                          {event.description || "—"}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        {event.high_volume ? (
                                          <Chip
                                            label="High volume"
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                          />
                                        ) : (
                                          <Chip
                                            label="Normal"
                                            size="small"
                                            variant="outlined"
                                          />
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Stack
                                          direction="row"
                                          spacing={0.5}
                                          justifyContent="flex-end"
                                          flexWrap="wrap"
                                        >
                                          <Tooltip title="Copy event name">
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                copyText(event.type)
                                              }
                                            >
                                              <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Stack>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </TabPanel>
          </>
        )}
      </Paper>

      <EndpointFormDialog
        open={endpointDialogOpen}
        onClose={() => setEndpointDialogOpen(false)}
        onSubmit={handleSaveEndpoint}
        saving={savingEndpoint}
        endpoint={editingEndpoint}
        catalogEvents={catalogEvents}
        clientOrganizations={clientOrganizations}
        targetDevices={targetDevices}
        targetDeviceTypes={targetDeviceTypes}
      />

      <Dialog
        open={Boolean(deleteEndpointDialog)}
        onClose={() => {
          if (!deletingEndpoint) setDeleteEndpointDialog(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Delete webhook endpoint?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will permanently delete{" "}
            <strong>{deleteEndpointDialog?.name || "this webhook"}</strong>.
            Delivery history and future event sends for this endpoint may be
            affected.
          </Typography>
          <Alert severity="warning">This action cannot be undone.</Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteEndpointDialog(null)}
            disabled={deletingEndpoint}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteEndpoint}
            disabled={deletingEndpoint}
            startIcon={
              deletingEndpoint ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {deletingEndpoint ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(secretDialog)}
        onClose={() => setSecretDialog(null)}
      >
        <DialogTitle>{secretDialog?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Store this secret securely. It will not be shown again.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={secretDialog?.secret || ""}
            InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={() => copyText(secretDialog?.secret || "")}
          >
            Copy
          </Button>
          <Button variant="contained" onClick={() => setSecretDialog(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(apiKeyDialog)}
        onClose={() => setApiKeyDialog(null)}
      >
        <DialogTitle>API key created — {apiKeyDialog?.name}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now. It cannot be retrieved later.
          </Alert>
          {apiKeyDialog?.scopes?.length ? (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>Scopes:</strong> {apiKeyDialog.scopes.join(", ")}
            </Typography>
          ) : null}
          <TextField
            fullWidth
            label="API key"
            value={apiKeyDialog?.api_key || ""}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: "monospace", fontSize: 13 },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={() => copyText(apiKeyDialog?.api_key || "")}
          >
            Copy
          </Button>
          <Button variant="contained" onClick={() => setApiKeyDialog(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(testDialogEndpoint)}
        onClose={() => setTestDialogEndpoint(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Test webhook — {testDialogEndpoint?.name}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Event type</InputLabel>
              <Select
                label="Event type"
                value={testEventType}
                onChange={(e) => setTestEventType(e.target.value)}
              >
                {(testDialogEndpoint?.subscribed_events?.length
                  ? testDialogEndpoint.subscribed_events
                  : ["device.created"]
                ).map((ev) => (
                  <MenuItem key={ev} value={ev}>
                    {ev}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Attributes (JSON)"
              multiline
              minRows={4}
              value={testAttributesJson}
              onChange={(e) => setTestAttributesJson(e.target.value)}
              helperText="Optional custom payload attributes merged into the test event"
              InputProps={{ sx: { fontFamily: "monospace", fontSize: 12 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogEndpoint(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTestEndpoint}
            disabled={testingEndpoint}
            startIcon={
              testingEndpoint ? (
                <CircularProgress size={16} />
              ) : (
                <PlayArrowIcon />
              )
            }
          >
            {testingEndpoint ? "Sending…" : "Send test"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={false} onClose={() => { }} maxWidth="sm" fullWidth>
        <DialogTitle>Delivery attempts</DialogTitle>
        <DialogContent dividers>
          {loadingAttempts ? (
            <CircularProgress size={28} />
          ) : attempts.length === 0 ? (
            <Typography color="text.secondary">
              No attempts recorded yet.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {attempts.map((a) => (
                <Paper key={a.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Attempt #{a.attempt_number} — HTTP{" "}
                    {a.response_status ?? "error"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.duration_ms != null ? `${a.duration_ms} ms` : ""}{" "}
                    {a.created_at
                      ? new Date(a.created_at).toLocaleString()
                      : ""}
                  </Typography>
                  {a.error_message ? (
                    <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                      {a.error_message}
                    </Typography>
                  ) : null}
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* <Snackbar
        open={Boolean(success)}
        autoHideDuration={4000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      </Snackbar> */}
      <Snackbar
  open={notification.open && notification.severity === "success"}
  autoHideDuration={4000}
  onClose={closeNotification}
  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
>
  <Alert severity="success" onClose={closeNotification}>
    {notification.message}
  </Alert>
</Snackbar>
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>

      {/* drawer */}

      <Drawer
        anchor="right"
        open={detailsOpen}
        onClose={handleCloseDetails}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 500, md: 600 },
          },
        }}
      >
        {selectedDelivery && (
          <>
            {/* Header */}
            <Box
              sx={{
                p: 2,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background:
                  "linear-gradient(135deg, #0f172a, #0f766e 60%, #082f49)",
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                Delivery Details
              </Typography>

              <IconButton onClick={handleCloseDetails} sx={{ color: "#fff" }}>
                <Close />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3 }}>
              <Stack spacing={2}>
                <DetailItem label="Delivery ID" value={selectedDelivery.id} />

                <DetailItem
                  label="Event ID"
                  value={selectedDelivery.webhook_event_id}
                />

                <DetailItem label="Status" value={selectedDelivery.status} />

                <DetailItem
                  label="Endpoint"
                  value={selectedDelivery.endpoint?.name}
                />

                <DetailItem
                  label="Target URL"
                  value={targetLabel(selectedDelivery.endpoint)}
                />

                <DetailItem
                  label="Event Type"
                  value={selectedDelivery.event_type}
                />

                <DetailItem
                  label="HTTP Status"
                  value={selectedDelivery.last_http_status}
                />

                <DetailItem
                  label="Attempts"
                  value={selectedDelivery.attempt_count}
                />

                <DetailItem
                  label="Published"
                  value={formatDateTime(selectedDelivery.event_occurred_at)}
                />

                <DetailItem
                  label="Queued"
                  value={formatDateTime(selectedDelivery.created_at)}
                />

                <DetailItem
                  label="Next Retry"
                  value={formatDateTime(selectedDelivery.next_retry_at)}
                />

                <Divider />

                <Typography fontWeight={700}>Error Message</Typography>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    fontFamily: "monospace",
                    fontSize: 13,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {selectedDelivery.last_error || "No error"}
                </Box>

                <Typography fontWeight={700}>Attributes</Typography>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    fontFamily: "monospace",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    overflow: "auto",
                  }}
                >
                  {JSON.stringify(selectedDelivery.attributes, null, 2)}
                </Box>

                <Typography fontWeight={700}>Related Resources</Typography>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    fontFamily: "monospace",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    overflow: "auto",
                  }}
                >
                  {JSON.stringify(selectedDelivery.related, null, 2)}
                </Box>

                <Divider />

                <Box>
                  <Typography fontWeight={700} sx={{ mb: 1 }}>
                    Delivery Attempts
                  </Typography>
                  {loadingAttempts ? (
                    <Box sx={{ py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : attempts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No attempts recorded yet.
                    </Typography>
                  ) : (
                    <Stack spacing={1.25}>
                      {attempts.map((attempt, index) => {
                        const isSuccess =
                          attempt.response_status &&
                          Number(attempt.response_status) < 400;
                        return (
                          <Paper
                            key={attempt.id}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderColor: isSuccess
                                ? "rgba(22,163,74,0.25)"
                                : "rgba(239,68,68,0.25)",
                              bgcolor: isSuccess
                                ? "rgba(240,253,244,0.72)"
                                : "rgba(254,242,242,0.72)",
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              spacing={2}
                              sx={{ mb: 0.75 }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 800 }}
                              >
                                Attempt #{attempt.attempt_number || index + 1}
                              </Typography>
                              <Chip
                                size="small"
                                label={`HTTP ${attempt.response_status ?? "error"}`}
                                color={isSuccess ? "success" : "error"}
                                variant="outlined"
                              />
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {attempt.duration_ms != null
                                ? `${attempt.duration_ms} ms`
                                : "No duration"}{" "}
                              {attempt.created_at
                                ? `• ${new Date(attempt.created_at).toLocaleString()}`
                                : ""}
                            </Typography>
                            {attempt.request_headers ? (
                              <>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    mt: 1,
                                    fontWeight: 700,
                                  }}
                                >
                                  Request headers
                                </Typography>
                                <Box
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {JSON.stringify(
                                    attempt.request_headers,
                                    null,
                                    2,
                                  )}
                                </Box>
                              </>
                            ) : null}
                            {attempt.response_headers ? (
                              <>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    mt: 1,
                                    fontWeight: 700,
                                  }}
                                >
                                  Response headers
                                </Typography>
                                <Box
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {JSON.stringify(
                                    attempt.response_headers,
                                    null,
                                    2,
                                  )}
                                </Box>
                              </>
                            ) : null}
                            {attempt.error_message ? (
                              <Typography
                                variant="body2"
                                color="error"
                                sx={{ mt: 1 }}
                              >
                                {attempt.error_message}
                              </Typography>
                            ) : null}
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Box>
          </>
        )}
      </Drawer>
    </Box>
  );
}
